'use client';

import React, { Ref } from 'react';
import Link, { LinkProps } from 'next/link';
import { usePathname } from 'next/navigation';
import { DEFAULT_LOCALE, LOCALIZATIONS_ARRAY } from '@/utils/type/localizations';

interface LocalizedLinkProps extends Omit<LinkProps, 'href'> {
  href: string | undefined;
  children?: React.ReactNode;
  className?: string;
  ref?: Ref<HTMLAnchorElement>;
}

export default function LocalizedLink({
  href = '/',
  children,
  className,
  ref,
  ...rest
}: LocalizedLinkProps) {
  const pathname = usePathname();
  const firstSegment = pathname?.split('/')?.[1];
  const currentLocale = LOCALIZATIONS_ARRAY.includes(
    (firstSegment ?? '') as (typeof LOCALIZATIONS_ARRAY)[number],
  )
    ? (firstSegment as (typeof LOCALIZATIONS_ARRAY)[number])
    : DEFAULT_LOCALE;

  // If href is undefined, default to '/'
  const safeHref = href || '/';

  const hasLocalePrefix = LOCALIZATIONS_ARRAY.some(
    loc => safeHref === `/${loc}` || safeHref.startsWith(`/${loc}/`),
  );
  const localizedHref = hasLocalePrefix
    ? safeHref
    : safeHref === '/'
      ? (`/${currentLocale}` as string)
      : `/${currentLocale}${safeHref.startsWith('/') ? '' : '/'}${safeHref}`;

  return (
    <Link href={localizedHref} className={className} ref={ref} {...rest}>
      {children}
    </Link>
  );
}
